import React, { useState, useMemo, useEffect } from "react";
import {
  Clock, ChevronRight, ChevronDown, X, CheckCircle2, AlertTriangle,
  UserCircle2, FileText, Languages, Link as LinkIcon,
} from "lucide-react";
import { SectionLabel, FlagBadge, ConfidencePill, FilterChip } from "./atoms";

/* ============================================================
   DNH Admin — Review Queue tab.
   Live data from /api/admin/review-queue.
   ============================================================ */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const REGIONS = ["All", "Asia", "Europe", "Americas", "MENA", "Africa", "Global"];
const FLAGS = ["All", "ready", "developing"];
const CONFIDENCES = ["All", "High", "Moderate", "Developing"];
const FILTER_LABEL = { ready: "Ready", developing: "Developing", All: "All" };

const DetailPanel = ({ row, onClose, onAction }) => (
  <div
    className="bg-[var(--surface-2)] border-t border-[var(--hairline)] p-6 lg:p-8"
    data-testid={`queue-detail-${row.id}`}
  >
    <div className="grid grid-cols-12 gap-8">
      <div className="col-span-12 lg:col-span-7">
        <div className="flex items-center justify-between gap-4">
          <FlagBadge flag={row.flag} />
          <button
            onClick={onClose}
            data-testid={`queue-detail-close-${row.id}`}
            className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-[var(--paper)] text-[var(--muted)] hover:text-[var(--ink)]"
            aria-label="Close detail"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>
        <h3 className="font-serif text-[22px] sm:text-[26px] leading-[1.25] text-[var(--ink)] mt-3">{row.headline}</h3>
        <p className="font-sans text-[14.5px] leading-[1.7] text-[var(--ink-2)] mt-3">{row.dek}</p>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="rounded-[10px] border border-[var(--hairline)] bg-[var(--paper)] p-4">
            <div className="font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--muted)]">Source</div>
            <div className="font-serif text-[18px] text-[var(--ink)] mt-1 leading-tight truncate">{row.source_name || row.sources}</div>
            <div className="font-mono text-[10.5px] text-[var(--ink-2)] mt-1">{row.region}</div>
          </div>
          <div className="rounded-[10px] border border-[var(--hairline)] bg-[var(--paper)] p-4">
            <div className="font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--muted)]">Cluster</div>
            <div className="font-serif text-[18px] text-[var(--ink)] mt-1 leading-tight">{row.cluster}</div>
            <div className="font-mono text-[10.5px] text-[var(--ink-2)] mt-1">{row.confidence}</div>
          </div>
        </div>

        {row.source_url && (
          <div className="mt-5">
            <SectionLabel>Source URL</SectionLabel>
            <a
              href={row.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 font-mono text-[11.5px] text-[var(--crimson)] hover:underline"
            >
              <LinkIcon size={11} strokeWidth={1.75} />
              {row.source_url.slice(0, 72)}{row.source_url.length > 72 ? "…" : ""}
            </a>
          </div>
        )}
      </div>

      <div className="col-span-12 lg:col-span-5">
        <div className="rounded-[12px] border border-[var(--hairline)] bg-[var(--paper)] p-5">
          <SectionLabel>Editor actions</SectionLabel>
          <div className="mt-4">
            <label className="font-mono text-[10.5px] tracking-[0.1em] uppercase text-[var(--muted)]">Confidence</label>
            <div className="mt-2 flex gap-2">
              {["High", "Moderate", "Developing"].map((c) => (
                <button
                  key={c}
                  data-testid={`queue-conf-${row.id}-${c.toLowerCase()}`}
                  className={`h-8 px-3 rounded-full font-mono text-[10.5px] tracking-[0.08em] uppercase transition-colors border ${
                    c === row.confidence
                      ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                      : "border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink-2)] hover:text-[var(--crimson)] hover:border-[var(--crimson)]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-5">
            <label className="font-mono text-[10.5px] tracking-[0.1em] uppercase text-[var(--muted)]">Assign editor</label>
            <select
              defaultValue=""
              data-testid={`queue-assign-${row.id}`}
              className="mt-2 w-full h-9 px-3 rounded-[8px] border border-[var(--hairline)] bg-[var(--surface-2)] font-mono text-[12px] text-[var(--ink)] outline-none focus:border-[var(--crimson)]"
            >
              <option value="">Unassigned</option>
              <option>A. Mehta</option>
              <option>L. Berger</option>
              <option>K. Nguyen</option>
              <option>C. Vargas</option>
              <option>T. Oliveira</option>
              <option>Y. Tanaka</option>
            </select>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              data-testid={`queue-publish-${row.id}`}
              onClick={() => onAction(row.id, "published")}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--ink)] text-[var(--paper)] font-mono text-[11px] tracking-[0.08em] uppercase hover:bg-[var(--crimson)] transition-colors"
            >
              <CheckCircle2 size={12} strokeWidth={1.75} /> Publish
            </button>
            <button
              data-testid={`queue-hold-${row.id}`}
              onClick={() => onAction(row.id, "held")}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] font-mono text-[11px] tracking-[0.08em] uppercase hover:border-[var(--ink)] transition-colors"
            >
              Hold
            </button>
            <button
              data-testid={`queue-escalate-${row.id}`}
              onClick={() => onAction(row.id, "escalated")}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[var(--crimson)] text-[var(--crimson)] font-mono text-[11px] tracking-[0.08em] uppercase hover:bg-[var(--crimson)] hover:text-[var(--paper)] transition-colors"
            >
              <AlertTriangle size={12} strokeWidth={1.75} /> Escalate
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const QueueRow = ({ row, selected, onSelect, expanded, onExpand, onAction }) => (
  <>
    <div
      data-testid={`admin-queue-row-${row.id}`}
      className={`grid grid-cols-12 gap-4 px-6 py-5 items-start transition-colors ${
        expanded ? "bg-[var(--surface-2)]" : "hover:bg-[var(--surface-2)]"
      } ${selected ? "bg-[var(--crimson)]/5" : ""}`}
    >
      <div className="col-span-12 lg:col-span-5 flex gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(row.id)}
          data-testid={`queue-select-${row.id}`}
          className="mt-1 accent-[var(--crimson)]"
        />
        <div>
          <FlagBadge flag={row.flag} />
          <h3 className="font-serif text-[16.5px] leading-[1.35] text-[var(--ink)] mt-2">{row.headline}</h3>
          <div className="font-mono text-[10.5px] text-[var(--muted)] mt-2 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><FileText size={10} strokeWidth={1.75} /> {row.source_name || "—"}</span>
            <span className="text-[var(--hairline-strong)]">·</span>
            <span className="inline-flex items-center gap-1"><UserCircle2 size={10} strokeWidth={1.75} /> {row.assignee}</span>
          </div>
        </div>
      </div>
      <div className="col-span-6 lg:col-span-2">
        <div className="font-mono text-[10.5px] tracking-[0.1em] uppercase text-[var(--ink-2)]">{row.cluster}</div>
        <div className="font-mono text-[10.5px] text-[var(--muted)] mt-1">{row.region}</div>
      </div>
      <div className="col-span-2 lg:col-span-1 font-mono text-[13px] text-[var(--ink)]">{row.sources}</div>
      <div className="col-span-4 lg:col-span-2"><ConfidencePill level={row.confidence} /></div>
      <div className="col-span-6 lg:col-span-1 font-mono text-[12.5px] text-[var(--crimson)]">
        <Clock size={11} strokeWidth={1.75} className="inline mr-1" />{row.sla}
      </div>
      <div className="col-span-6 lg:col-span-1 flex lg:justify-end">
        <button
          onClick={() => onExpand(row.id)}
          data-testid={`admin-open-${row.id}`}
          className="inline-flex items-center gap-1 font-mono text-[11px] tracking-[0.08em] uppercase text-[var(--ink-2)] hover:text-[var(--crimson)] transition-colors"
        >
          {expanded ? "Close" : "Open"}
          {expanded ? <ChevronDown size={12} strokeWidth={2} /> : <ChevronRight size={12} strokeWidth={2} />}
        </button>
      </div>
    </div>
    {expanded && <DetailPanel row={row} onClose={() => onExpand(null)} onAction={onAction} />}
  </>
);

export default function ReviewQueue() {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("All");
  const [flag, setFlag] = useState("All");
  const [conf, setConf] = useState("All");
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [actioned, setActioned] = useState({ published: 0, held: 0, escalated: 0 });

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/review-queue?per_page=100`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { setAllRows(d.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleAction = async (storyId, status) => {
    try {
      await fetch(`${BASE_URL}/api/admin/story/${storyId}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      setAllRows(prev => prev.filter(r => r.id !== storyId));
      setSelected(prev => prev.filter(id => id !== storyId));
      setExpanded(null);
      setActioned(prev => ({ ...prev, [status]: (prev[status] || 0) + 1 }));
    } catch { /* ignore */ }
  };

  const handleBulkPublish = async () => {
    await Promise.all(selected.map(id => handleAction(id, "published")));
  };

  const rows = useMemo(
    () =>
      allRows
        .filter(r => region === "All" || r.region === region)
        .filter(r => flag === "All" || r.flag === flag)
        .filter(r => conf === "All" || r.confidence === conf),
    [allRows, region, flag, conf]
  );

  const toggleSelect = (id) =>
    setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  return (
    <section data-testid="admin-review-queue" className="border-b border-[var(--hairline)]">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-10 lg:py-14">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3" data-testid="queue-filters">
          <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)] mr-2">Region</span>
          {REGIONS.map(r => (
            <FilterChip key={r} active={region === r} onClick={() => setRegion(r)} testId={`filter-region-${r.toLowerCase()}`}>{r}</FilterChip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)] mr-2">Flag</span>
          {FLAGS.map(f => (
            <FilterChip key={f} active={flag === f} onClick={() => setFlag(f)} testId={`filter-flag-${f.toLowerCase()}`}>{FILTER_LABEL[f] || f}</FilterChip>
          ))}
          <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)] ml-4 mr-2">Confidence</span>
          {CONFIDENCES.map(c => (
            <FilterChip key={c} active={conf === c} onClick={() => setConf(c)} testId={`filter-conf-${c.toLowerCase()}`}>{c}</FilterChip>
          ))}
        </div>

        {/* Actioned counter */}
        {(actioned.published > 0 || actioned.held > 0 || actioned.escalated > 0) && (
          <div data-testid="queue-actioned-banner" className="mt-4 rounded-[10px] border border-[var(--hairline)] bg-[var(--surface-2)] px-5 py-3 flex items-center gap-5 flex-wrap">
            <span className="font-mono text-[10.5px] tracking-[0.1em] uppercase text-[var(--muted)]">This session:</span>
            {actioned.published > 0 && <span className="font-mono text-[11px] text-[var(--ink)]"><CheckCircle2 size={11} strokeWidth={1.75} className="inline mr-1 text-green-600" />{actioned.published} published</span>}
            {actioned.held > 0 && <span className="font-mono text-[11px] text-[var(--ink)]">{actioned.held} held</span>}
            {actioned.escalated > 0 && <span className="font-mono text-[11px] text-[var(--crimson)]"><AlertTriangle size={11} strokeWidth={1.75} className="inline mr-1" />{actioned.escalated} escalated</span>}
          </div>
        )}

        {/* Bulk action bar */}
        {selected.length > 0 && (
          <div
            data-testid="queue-bulk-bar"
            className="mt-6 rounded-[12px] border border-[var(--crimson)] bg-[var(--crimson)]/5 px-5 py-3 flex items-center justify-between flex-wrap gap-3"
          >
            <span className="font-mono text-[11.5px] text-[var(--ink)]">{selected.length} selected</span>
            <div className="flex items-center gap-2">
              <button className="h-8 px-3 rounded-full bg-[var(--ink)] text-[var(--paper)] font-mono text-[10.5px] tracking-[0.08em] uppercase hover:bg-[var(--crimson)] transition-colors" data-testid="queue-bulk-publish" onClick={handleBulkPublish}>Publish all</button>
              <button className="h-8 px-3 rounded-full border border-[var(--hairline-strong,#D8D2C6)] font-mono text-[10.5px] tracking-[0.08em] uppercase text-[var(--ink)] hover:border-[var(--ink)] transition-colors" data-testid="queue-bulk-assign">Assign…</button>
              <button onClick={() => setSelected([])} className="h-8 px-3 rounded-full border border-[var(--hairline)] font-mono text-[10.5px] tracking-[0.08em] uppercase text-[var(--muted)] hover:text-[var(--ink)] transition-colors" data-testid="queue-bulk-clear">Clear</button>
            </div>
          </div>
        )}

        {/* Queue */}
        <div className="mt-7 border border-[var(--hairline)] rounded-[14px] overflow-hidden">
          <div className="hidden lg:grid grid-cols-12 gap-4 bg-[var(--surface-2)] px-6 py-3 border-b border-[var(--hairline)]">
            <div className="col-span-5 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)]">Story</div>
            <div className="col-span-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)]">Cluster · Region</div>
            <div className="col-span-1 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)]">Src</div>
            <div className="col-span-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)]">Confidence</div>
            <div className="col-span-1 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)]">Age</div>
            <div className="col-span-1 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)] text-right">—</div>
          </div>

          {loading ? (
            <div className="px-6 py-12 flex flex-col gap-4">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-16 rounded-[8px] animate-pulse bg-[var(--surface-2)]" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-10 text-center font-mono text-[11px] text-[var(--muted)]" data-testid="queue-empty">
              No items match the current filters.
            </div>
          ) : (
            rows.map((row, i) => (
              <div key={row.id} className={i < rows.length - 1 ? "border-b border-[var(--hairline)]" : ""}>
                <QueueRow
                  row={row}
                  selected={selected.includes(row.id)}
                  onSelect={toggleSelect}
                  expanded={expanded === row.id}
                  onExpand={(id) => setExpanded(id === expanded ? null : id)}
                  onAction={handleAction}
                />
              </div>
            ))
          )}
        </div>

        {!loading && (
          <p className="mt-4 font-mono text-[10.5px] tracking-[0.1em] uppercase text-[var(--muted)]">
            Showing {rows.length} of {allRows.length} signals · Live from DB
          </p>
        )}
      </div>
    </section>
  );
}
